import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Card, Table, ListGroup, Accordion } from 'react-bootstrap';
import axios from 'axios';
import { EquipmentFormData, Subcategory, FullEquipment, Category, EquipmentToEquipmentComplementaryItem, EquipmentToConsumableComplementaryItem, Consumable } from '../interfaces';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import _ from 'lodash';
// אייקונים
import { 
  faSave, 
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { API_BASE_URL, buildApiUrl } from '../config/api';

interface ComplementaryEquipmentFormItem {
  id: number;
  name: string;
  category_name: string;
  subcategory_name: string;
  quantity: number;
}

interface ComplementaryConsumableFormItem {
  id: number;
  name: string;
  quantity: number;
}

interface EquipmentFormProps {
  equipmentToEdit?: FullEquipment | null;
  onSuccess: (savedEquipmentName?: string) => void;
  onCancel: () => void;
}

// פונקציה עזר רגילה, מחוץ לקומפוננטה
const groupEquipment = (equipment: FullEquipment[]) => {
    const grouped: { [category: string]: { [subcategory: string]: FullEquipment[] } } = {};
    equipment.forEach(item => {
        const category = item.category_name || 'Uncategorized';
        const subcategory = item.subcategory_name || 'Uncategorized';
        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
        grouped[category][subcategory].push(item);
    });
    return grouped;
};

const EquipmentForm: React.FC<EquipmentFormProps> = ({ equipmentToEdit, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<EquipmentFormData>({
    name: '',
    subcategory: '',
    quantity: 1,
    case_quantity: 1,
    is_heavy: false,
    location_in_warehouse: null,
    image_url: null,
    is_defective: false,
    defect_description: null,
    notes: null,
    complementary_equipment_data: [],
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [allEquipment, setAllEquipment] = useState<FullEquipment[]>([]);

  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [filteredSubcategories, setFilteredSubcategories] = useState<Subcategory[]>([]);

  const [selectedCompCategory, setSelectedCompCategory] = useState<number | ''>('');
  const [filteredCompSubcategories, setFilteredCompSubcategories] = useState<Subcategory[]>([]);
  const [selectedCompSubcategory, setSelectedCompSubcategory] = useState<number | ''>('');
  const [availableCompEquipment, setAvailableCompEquipment] = useState<FullEquipment[]>([]);
  const [selectedCompEquipmentId, setSelectedCompEquipmentId] = useState<number | ''>('');
  const [compEquipmentQuantity, setCompEquipmentQuantity] = useState<number>(1);
  const [currentComplementaryItems, setCurrentComplementaryItems] = useState<ComplementaryEquipmentFormItem[]>([]);
  const [allConsumables, setAllConsumables] = useState<Consumable[]>([]);
  const [selectedConsumableId, setSelectedConsumableId] = useState<number | ''>('');
  const [compConsumableQuantity, setCompConsumableQuantity] = useState<number>(1);
  const [currentComplementaryConsumables, setCurrentComplementaryConsumables] = useState<ComplementaryConsumableFormItem[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loadingDeps, setLoadingDeps] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');


  useEffect(() => {
    const fetchDependencies = async () => {
      try {
        const [categoriesRes, subcategoriesRes, allEquipmentRes, consumablesRes] = await Promise.all([
          axios.get(buildApiUrl('settings/categories/')),
          axios.get(buildApiUrl('settings/subcategories/')),
          axios.get(buildApiUrl('inventory/equipment/')),
          axios.get<Consumable[]>(buildApiUrl('settings/consumables/')),
        ]);
        setCategories(categoriesRes.data);
        setSubcategories(subcategoriesRes.data);
        setAllEquipment(allEquipmentRes.data);
        setAllConsumables(consumablesRes.data);
      } catch (err) {
        setError("לא ניתן לטעון נתונים נדרשים לטופס.");
      } finally {
        setLoadingDeps(false);
      }
    };
    fetchDependencies();
  }, []);

  useEffect(() => {
    if (equipmentToEdit) {
      setFormData({
        id: equipmentToEdit.id,
        name: equipmentToEdit.name || '',
        subcategory: equipmentToEdit.subcategory || '',
        quantity: equipmentToEdit.quantity || 1,
        case_quantity: equipmentToEdit.case_quantity || 1,
        is_heavy: equipmentToEdit.is_heavy || false,
        location_in_warehouse: equipmentToEdit.location_in_warehouse || '',
        image_url: equipmentToEdit.image_url || '',
        is_defective: equipmentToEdit.is_defective,
        defect_description: equipmentToEdit.defect_description || '',
        notes: equipmentToEdit.notes || '',
        complementary_equipment_data: [],
      });
      
      const initialSubcategory = subcategories.find(s => s.id === equipmentToEdit.subcategory);
      if (initialSubcategory) {
        setSelectedCategory(initialSubcategory.category);
      }

      const initialCompItems: ComplementaryEquipmentFormItem[] = (equipmentToEdit.complementary_items || [])
        .map((item: EquipmentToEquipmentComplementaryItem) => {
          const compEquip = allEquipment.find(eq => eq.id === item.complementary_equipment);
          if (!compEquip) {
            console.warn(`Complementary equipment with ID ${item.complementary_equipment} not found.`);
            return null;
          }
          return {
            id: compEquip.id!,
            name: compEquip.name,
            category_name: compEquip.category_name,
            subcategory_name: compEquip.subcategory_name,
            quantity: item.quantity
          };
        })
        .filter((item): item is ComplementaryEquipmentFormItem => item !== null);
      setCurrentComplementaryItems(initialCompItems);

      const initialCons: ComplementaryConsumableFormItem[] = (equipmentToEdit.complementary_consumable_items || []).map(
        (item: EquipmentToConsumableComplementaryItem) => ({
          id: item.consumable,
          name: item.consumable_name || `מתכלה #${item.consumable}`,
          quantity: item.quantity,
        })
      );
      setCurrentComplementaryConsumables(initialCons);

    } else {
      setFormData({
        name: '', subcategory: '', quantity: 1, case_quantity: 1, is_heavy: false,
        location_in_warehouse: null, image_url: null, is_defective: false,
        defect_description: null, notes: null, 
      });
      setSelectedCategory('');
      setCurrentComplementaryItems([]);
      setCurrentComplementaryConsumables([]);
      setSelectedConsumableId('');
    }
  }, [equipmentToEdit, subcategories, allEquipment]);

  useEffect(() => {
    if (selectedCategory) {
      setFilteredSubcategories(subcategories.filter(sub => sub.category === Number(selectedCategory)));
      // רק אם זה לא עריכה של ציוד קיים, אפס את תת-הקטגוריה אם היא לא שייכת לקטגוריה החדשה
      if (!equipmentToEdit && formData.subcategory && !subcategories.some(s => s.id === formData.subcategory && s.category === Number(selectedCategory))) {
        setFormData(prev => ({ ...prev, subcategory: '' }));
      }
    } else {
      setFilteredSubcategories([]);
      // רק אם זה לא עריכה של ציוד קיים, אפס את תת-הקטגוריה
      if (!equipmentToEdit) {
        setFormData(prev => ({ ...prev, subcategory: '' }));
      }
    }
  }, [selectedCategory, subcategories, equipmentToEdit]);

  useEffect(() => {
    if (selectedCompCategory) {
      setFilteredCompSubcategories(subcategories.filter(sub => sub.category === Number(selectedCompCategory)));
      setSelectedCompSubcategory('');
    } else {
      setFilteredCompSubcategories([]);
      setSelectedCompSubcategory('');
    }
  }, [selectedCompCategory, subcategories]);

  useEffect(() => {
    if (selectedCompSubcategory) {
      const filtered = allEquipment.filter(eq => 
        eq.subcategory === Number(selectedCompSubcategory) && eq.id !== equipmentToEdit?.id
      );
      setAvailableCompEquipment(filtered);
      setSelectedCompEquipmentId('');
    } else {
      setAvailableCompEquipment([]);
      setSelectedCompEquipmentId('');
    }
  }, [selectedCompSubcategory, allEquipment, equipmentToEdit]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'quantity' || name === 'case_quantity') {
        setFormData({ ...formData, [name]: parseInt(value) || 0 });
    } else if (name === 'subcategory') {
        setFormData({ ...formData, [name]: value === '' ? '' : parseInt(value) });
    }
    else {
      setFormData({ ...formData, [name]: value === '' ? null : value });
    }
  };

  const handleAddComplementaryItem = () => {
    if (selectedCompEquipmentId && compEquipmentQuantity > 0) {
      const itemToAdd = allEquipment.find(eq => eq.id === Number(selectedCompEquipmentId));
      if (itemToAdd) {
        const existingItemIndex = currentComplementaryItems.findIndex(item => item.id === itemToAdd.id);
        if (existingItemIndex > -1) {
          const updatedItems = [...currentComplementaryItems];
          updatedItems[existingItemIndex].quantity += compEquipmentQuantity;
          setCurrentComplementaryItems(updatedItems);
        } else {
          if (itemToAdd.id === undefined) {
             console.error("itemToAdd.id is undefined");
             return;
          }
          setCurrentComplementaryItems([
            ...currentComplementaryItems,
            {
              id: itemToAdd.id,
              name: itemToAdd.name,
              category_name: itemToAdd.category_name,
              subcategory_name: itemToAdd.subcategory_name,
              quantity: compEquipmentQuantity,
            },
          ]);
        }
        setSelectedCompCategory('');
        setSelectedCompSubcategory('');
        setSelectedCompEquipmentId('');
        setCompEquipmentQuantity(1);
        setError(null);
      }
    } else {
      setError("אנא בחר/י פריט משלים והזן/י כמות חוקית.");
    }
  };

  const handleRemoveComplementaryItem = (id: number) => {
    setCurrentComplementaryItems(currentComplementaryItems.filter(item => item.id !== id));
  };

  const handleAddComplementaryConsumable = () => {
    if (selectedConsumableId && compConsumableQuantity > 0) {
      const c = allConsumables.find((x) => x.id === Number(selectedConsumableId));
      if (c && c.id !== undefined) {
        const idx = currentComplementaryConsumables.findIndex((x) => x.id === c.id);
        if (idx > -1) {
          const next = [...currentComplementaryConsumables];
          next[idx] = { ...next[idx], quantity: next[idx].quantity + compConsumableQuantity };
          setCurrentComplementaryConsumables(next);
        } else {
          setCurrentComplementaryConsumables([
            ...currentComplementaryConsumables,
            { id: c.id, name: c.name, quantity: compConsumableQuantity },
          ]);
        }
        setSelectedConsumableId('');
        setCompConsumableQuantity(1);
        setError(null);
      }
    } else {
      setError('אנא בחר/י מתכלה והזן/י כמות חוקית.');
    }
  };

  const handleRemoveComplementaryConsumable = (id: number) => {
    setCurrentComplementaryConsumables(currentComplementaryConsumables.filter((x) => x.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitLoading(true);

    if (!formData.name || !formData.subcategory) {
      setError("שם הפריט ותת-הקטגוריה הם שדות חובה.");
      setSubmitLoading(false);
      return;
    }
    
    // בנה את ה-payload לשליחה
    const dataToSend: any = { 
      ...formData,
      complementary_equipment_data: currentComplementaryItems.map(item => ({
        complementary_equipment: item.id,
        quantity: item.quantity,
      })),
      complementary_consumable_data: currentComplementaryConsumables.map((item) => ({
        consumable: item.id,
        quantity: item.quantity,
      })),
      purchase_date: null,
      rental_price_per_day: null,
      recommended_for_locations_tags: null,
      serial_numbers: [],
    };
    
    delete dataToSend.category_name;
    delete dataToSend.subcategory_name;
    
    try {
      if (equipmentToEdit?.id) {
        const response = await axios.put(buildApiUrl(`inventory/equipment/${equipmentToEdit.id}/`), dataToSend);
        const savedEquipmentName = response.data?.name || formData.name || equipmentToEdit.name;
        onSuccess(savedEquipmentName || undefined);
      } else {
        const response = await axios.post(buildApiUrl('inventory/equipment/'), dataToSend);
        const savedEquipmentName = response.data?.name || formData.name;
        onSuccess(savedEquipmentName || undefined);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response && err.response.data) {
        let errorMessages = '';
        for (const key in err.response.data) {
          if (Object.prototype.hasOwnProperty.call(err.response.data, key)) {
            const errorValue = err.response.data[key];
            if (Array.isArray(errorValue)) {
              errorMessages += `${key}: ${errorValue.join(', ')}\n`;
            } else {
              errorMessages += `${key}: ${errorValue}\n`;
            }
          }
        }
        setError(`שגיאה בשמירת הציוד: \n${errorMessages || 'שגיאה לא ידועה.'}`);
      } else {
        setError("שגיאה בשמירת הציוד. אנא נסה שוב.");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // קריאה ל-useMemo לפני ה-return, מחוץ לכל לוגיקה תנאית.
  const groupedInventory = useMemo(() => groupEquipment(allEquipment), [allEquipment]);

  if (loadingDeps) {
      return <Spinner animation="border" />;
  }

  return (
    <Form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm bg-light">
      <h3 className="mb-4">{equipmentToEdit ? 'עריכת ציוד' : 'הוספת ציוד חדש'}</h3>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Row className="mb-3">
        <Form.Group as={Col} md={8} controlId="formName">
          <Form.Label>שם הפריט</Form.Label>
          <Form.Control type="text" name="name" value={formData.name || ''} onChange={handleChange} required />
        </Form.Group>

        <Form.Group as={Col} md={4} controlId="formQuantity">
          <Form.Label>כמות במלאי</Form.Label>
          <Form.Control type="number" name="quantity" value={formData.quantity} onChange={handleChange} required min="0" />
        </Form.Group>
      </Row>

      <Row className="mb-3">
        <Form.Group as={Col} controlId="formCategory">
          <Form.Label>קטגוריה</Form.Label>
          <Form.Select
            name="category"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(parseInt(e.target.value) || '')}
            disabled={loadingDeps}
            required
          >
            <option value="">בחר קטגוריה...</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.category_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group as={Col} controlId="formSubcategory">
          <Form.Label>תת-קטגוריה</Form.Label>
          <Form.Select
            name="subcategory"
            value={formData.subcategory}
            onChange={handleChange}
            required
            disabled={loadingDeps || !selectedCategory}
          >
            <option value="">בחר תת-קטגוריה...</option>
            {filteredSubcategories.map(subcat => (
              <option key={subcat.id} value={subcat.id}>
                {subcat.subcategory_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
      
      
      <Row className="mb-3">
        <Form.Group as={Col} controlId="formLocationInWarehouse">
          <Form.Label>מיקום במחסן</Form.Label>
          <Form.Control type="text" name="location_in_warehouse" value={formData.location_in_warehouse || ''} onChange={handleChange} />
        </Form.Group>
        <Form.Group as={Col} controlId="formImageUrl">
          <Form.Label>קישור לתמונה (URL)</Form.Label>
          <Form.Control type="url" name="image_url" value={formData.image_url || ''} onChange={handleChange} />
        </Form.Group>
      </Row>
      
      <Row className="mb-3">
        
        <Col md={3} className="mt-4">
          <Form.Check type="checkbox" name="is_heavy" label="האם ציוד כבד?" checked={formData.is_heavy} onChange={handleChange} />
        </Col>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col} md={6} controlId="formCaseQuantity">
            <Form.Label>כמות יחידות בקייס/ארגז</Form.Label>
            <Form.Control type="number" name="case_quantity" value={formData.case_quantity} onChange={handleChange} min="1" />
        </Form.Group>
      </Row>

      <Form.Group className="mb-3" controlId="formNotes">
        <Form.Label>הערות</Form.Label>
        <Form.Control as="textarea" rows={3} name="notes" value={formData.notes || ''} onChange={handleChange} />
      </Form.Group>
      
      <Card className="mt-4 mb-4">
        <Card.Header>ניהול ציוד משלים</Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group controlId="compCategory">
                <Form.Label>קטגוריה</Form.Label>
                <Form.Select
                  value={selectedCompCategory}
                  onChange={(e) => setSelectedCompCategory(parseInt(e.target.value) || '')}
                  disabled={loadingDeps}
                >
                  <option value="">בחר קטגוריה...</option>
                  {categories.map(cat => (
                    <option key={`comp-cat-${cat.id}`} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="compSubcategory">
                <Form.Label>תת-קטגוריה</Form.Label>
                <Form.Select
                  value={selectedCompSubcategory}
                  onChange={(e) => setSelectedCompSubcategory(parseInt(e.target.value) || '')}
                  disabled={loadingDeps || !selectedCompCategory}
                >
                  <option value="">בחר תת-קטגוריה...</option>
                  {filteredCompSubcategories.map(subcat => (
                    <option key={`comp-sub-${subcat.id}`} value={subcat.id}>
                      {subcat.subcategory_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="compEquipment">
                <Form.Label>פריט ציוד</Form.Label>
                <Form.Select
                  value={selectedCompEquipmentId}
                  onChange={(e) => setSelectedCompEquipmentId(parseInt(e.target.value) || '')}
                  disabled={loadingDeps || !selectedCompSubcategory}
                >
                  <option value="">בחר פריט...</option>
                  {availableCompEquipment.map(item => (
                    <option key={`comp-equip-${item.id}`} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="compQuantity">
                <Form.Label>כמות נדרשת</Form.Label>
                <Form.Control
                  type="number"
                  value={compEquipmentQuantity}
                  onChange={(e) => setCompEquipmentQuantity(parseInt(e.target.value) || 1)}
                  min="1"
                  disabled={!selectedCompEquipmentId}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                onClick={handleAddComplementaryItem}
                disabled={!selectedCompEquipmentId || compEquipmentQuantity <= 0}
                className="w-100"
              >
                הוסף פריט משלים
              </Button>
            </Col>
          </Row>
          {currentComplementaryItems.length > 0 && (
            <div className="mt-4">
              <h6>פריטים משלימים שנבחרו:</h6>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>שם פריט</th>
                    <th>קטגוריה</th>
                    <th>תת-קטגוריה</th>
                    <th>כמות</th>
                    <th>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {currentComplementaryItems.map(item => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.category_name}</td>
                      <td>{item.subcategory_name}</td>
                      <td>{item.quantity}</td>
                      <td>
                        <Button variant="danger" size="sm" onClick={() => handleRemoveComplementaryItem(item.id)}>
                          הסר
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mt-4 mb-4">
        <Card.Header>ניהול מתכלים משלימים</Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group controlId="compConsumableSelect">
                <Form.Label>מתכלה</Form.Label>
                <Form.Select
                  value={selectedConsumableId === '' ? '' : String(selectedConsumableId)}
                  onChange={(e) => setSelectedConsumableId(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  disabled={loadingDeps}
                >
                  <option value="">בחר מתכלה...</option>
                  {[...allConsumables]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="compConsumableQty">
                <Form.Label>כמות נדרשת</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={compConsumableQuantity}
                  onChange={(e) => setCompConsumableQuantity(parseInt(e.target.value, 10) || 1)}
                  disabled={!selectedConsumableId}
                />
              </Form.Group>
            </Col>
          </Row>
          <Button
            variant="outline-primary"
            onClick={handleAddComplementaryConsumable}
            disabled={!selectedConsumableId || compConsumableQuantity <= 0}
            className="mb-3"
          >
            הוסף מתכלה משלימה
          </Button>
          {currentComplementaryConsumables.length > 0 && (
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>שם</th>
                  <th>כמות</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {currentComplementaryConsumables.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>
                      <Button variant="danger" size="sm" onClick={() => handleRemoveComplementaryConsumable(item.id)}>
                        הסר
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <div className="d-flex justify-content-end">
        <Button variant="secondary" onClick={onCancel} className="me-2" disabled={submitLoading}>
          <FontAwesomeIcon icon={faTimes} className="me-1" />
          ביטול
        </Button>
        <Button variant="primary" type="submit" disabled={submitLoading}>
          {submitLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" /> {equipmentToEdit ? 'עדכן ציוד' : 'הוסף ציוד'}</>}
        </Button>
      </div>
    </Form>
  );
};

export default EquipmentForm;
